import React from 'react';

/**
 * Code-native technical illustrations in a single publication style:
 * navy/graphite linework, copper accents, aluminum fills, square corners,
 * 1–1.5px rules, restrained measurement ticks, no gradients or motion.
 * All drawings are conceptual and imply no exact product geometry.
 */

const NAVY = '#0B1420';
const GRAPHITE = '#111820';
const COPPER = '#B66A33';
const ALUMINUM = '#C7CDD3';

/* Compact horizontal silhouettes (product-system card title rows).
   Tight viewBox cropped around the object drawing — no field, no frame. */
const compactSvgProps = {
  'aria-hidden': true as const,
  focusable: 'false' as const,
  preserveAspectRatio: 'xMidYMid meet',
  viewBox: '0 0 264 104',
  className: 'block h-auto w-full'
};
function TickRow({
  y,
  from,
  to,
  step = 16,
  height = 5
}: {
  y: number;
  from: number;
  to: number;
  step?: number;
  height?: number;
}) {
  const ticks: number[] = [];
  for (let x = from; x <= to; x += step) ticks.push(x);
  return <g stroke={GRAPHITE} strokeOpacity="0.55" strokeWidth="1" data-id="element-372" data-mp="ipe0jtyiqkwh">
      <line x1={from} y1={y} x2={to} y2={y} data-id="element-373" data-mp="iqytjf56pvs8" />
      {ticks.map((x, i) => <line key={x} x1={x} y1={y} x2={x} y2={y - (i % 2 === 0 ? height : height / 2)} data-id="element-374" data-mp="mrirgkxl13ff" data-mpi={i} />)}
    </g>;
}

/* A — Compact kit line-up: holder, insert group, driver, replacement screws */
export function StarterKitIllustration() {
  return <svg {...compactSvgProps} data-id="element-375" data-mp="11eu5cth8kzip">
      {/* holder silhouette */}
      <g stroke={NAVY} strokeWidth="1.5" data-id="element-376" data-mp="16e6z7y6iz6us">
        <path d="M4 16 H104 V40 H4 Z" fill={GRAPHITE} fillOpacity="0.9" data-id="element-377" data-mp="16d5cgwg5cvx8" />
        <path d="M104 12 H130 L140 28 L130 44 H104 Z" fill={GRAPHITE} fillOpacity="0.7" data-id="element-378" data-mp="1ahd2qy296jxk" />
      </g>
      <path d="M126 22 L138 28 L126 34 Z" fill={COPPER} data-id="element-379" data-mp="1fdak97p95wbx" />
      {/* insert group */}
      <g stroke={NAVY} strokeWidth="1.5" data-id="element-380" data-mp="3c980e9rocnws">
        <path d="M4 78 L22 62 L40 78 L22 94 Z" fill={ALUMINUM} fillOpacity="0.6" data-id="element-381" data-mp="3h6hxmblb23v6" />
        <path d="M42 78 L60 62 L78 78 L60 94 Z" fill={ALUMINUM} fillOpacity="0.42" data-id="element-382" data-mp="3h8kw7k0ix5bi" />
        <path d="M80 78 L98 62 L116 78 L98 94 Z" fill={ALUMINUM} fillOpacity="0.6" data-id="element-383" data-mp="jdgari115le9" />
      </g>
      {/* driver silhouette */}
      <g stroke={NAVY} strokeWidth="1.5" data-id="element-384" data-mp="xe0pa1zwwv1y">
        <path d="M150 14 H164 V42 H150 Z" fill={GRAPHITE} fillOpacity="0.6" data-id="element-385" data-mp="xav42xuemdbi" />
        <path d="M164 20 H244 V36 H164 Z" fill={GRAPHITE} fillOpacity="0.9" data-id="element-386" data-mp="scbgyu42qvhk" />
      </g>
      <line x1="246" y1="28" x2="260" y2="28" stroke={COPPER} strokeWidth="1.5" data-id="element-387" data-mp="179uae9rp5wvc" />
      {/* replacement screws */}
      <g stroke={NAVY} strokeWidth="1.5" data-id="element-388" data-mp="1g3yxci3pmo5l">
        <path d="M150 68 H190 V88 H150 Z" fill={ALUMINUM} fillOpacity="0.6" data-id="element-389" data-mp="1g2nuqlm03mg2" />
        <path d="M190 73 H208 L214 78 L208 83 H190 Z" fill="#FFFFFF" data-id="element-390" data-mp="1c2x026tcsm69" />
        <path d="M216 68 H252 V88 H216 Z" fill={ALUMINUM} fillOpacity="0.6" data-id="element-391" data-mp="1q13mmqsuqzqm" />
      </g>
      <g stroke={COPPER} strokeWidth="1.5" data-id="element-392" data-mp="1l2jwsfb513sz">
        <line x1="158" y1="78" x2="182" y2="78" data-id="element-393" data-mp="1l3v0yg8hwdkm" />
        <line x1="224" y1="78" x2="244" y2="78" data-id="element-394" data-mp="24oc1c1lddn7c" />
      </g>
    </svg>;
}

/* B — Compact paired holder silhouettes (two shank sizes) */
export function HolderPairIllustration() {
  return <svg {...compactSvgProps} data-id="element-395" data-mp="29l2fgjdm68q4">
      {/* upper holder — larger shank */}
      <g stroke={NAVY} strokeWidth="1.5" data-id="element-396" data-mp="1upwbs31zqlzy">
        <path d="M4 12 H170 V42 H4 Z" fill={GRAPHITE} fillOpacity="0.9" data-id="element-397" data-mp="1zoz6ouo9bbu7" />
        <path d="M170 15 H208 L222 27 L208 39 H170 Z" fill={GRAPHITE} fillOpacity="0.72" data-id="element-398" data-mp="1zr24w1zkcba0" />
      </g>
      <path d="M206 20 L220 27 L206 34 Z" fill={COPPER} data-id="element-399" data-mp="1qoswbhnuurz9" />
      <TickRow y={54} from={4} to={170} step={24} height={5} data-id="element-400" data-mp="1qqdqv7a66ax4" />
      {/* lower holder — smaller shank */}
      <g stroke={NAVY} strokeWidth="1.5" data-id="element-401" data-mp="1bxtqufibgjjw">
        <path d="M4 72 H150 V94 H4 Z" fill={GRAPHITE} fillOpacity="0.9" data-id="element-402" data-mp="1gzsuzrlvrkhw" />
        <path d="M150 74 H182 L194 83 L182 92 H150 Z" fill={GRAPHITE} fillOpacity="0.72" data-id="element-403" data-mp="12z8e50zizyr1" />
      </g>
      <path d="M181 78 L193 83 L181 88 Z" fill={COPPER} data-id="element-404" data-mp="16za4wf3q37rj" />
    </svg>;
}

/* C — Compact insert pair: clean face and chip-control face */
export function InsertPairIllustration() {
  return <svg {...compactSvgProps} data-id="element-405" data-mp="uzbuhankp04u">
      {/* clean face */}
      <g stroke={NAVY} strokeWidth="1.5" data-id="element-406" data-mp="q7ka5hv0fqn5">
        <path d="M56 6 L102 52 L56 98 L10 52 Z" fill={ALUMINUM} fillOpacity="0.5" data-id="element-407" data-mp="q697jldawoxm" />
      </g>
      <path d="M56 20 L88 52 L56 84 L24 52 Z" fill="none" stroke={NAVY} strokeOpacity="0.55" strokeWidth="1" data-id="element-408" data-mp="l7fu1sy0kxxg" />
      <circle cx="56" cy="52" r="5" fill={GRAPHITE} data-id="element-409" data-mp="c7soqw25z7z4" />
      <path d="M56 6 L102 52" stroke={COPPER} strokeWidth="1.5" data-id="element-410" data-mp="c6ztar8no4wa" />
      {/* chip-control face */}
      <g stroke={NAVY} strokeWidth="1.5" data-id="element-411" data-mp="79hggprip5j0">
        <path d="M170 6 L216 52 L170 98 L124 52 Z" fill={ALUMINUM} fillOpacity="0.5" data-id="element-412" data-mp="29mosjzx4bww" />
      </g>
      <g fill="none" stroke={NAVY} strokeOpacity="0.6" strokeWidth="1" data-id="element-413" data-mp="21mz8oq2vaqkd">
        <path d="M170 18 L204 52 L170 86 L136 52 Z" data-id="element-414" data-mp="1wnmibvcsf1d1" />
        <path d="M170 30 L192 52 L170 74 L148 52 Z" data-id="element-415" data-mp="1wo5q4j8q49dc" />
      </g>
      <path d="M144 42 C 158 34, 182 34, 196 42" fill="none" stroke={GRAPHITE} strokeWidth="1.5" data-id="element-416" data-mp="2556btij1hcxy" />
      <circle cx="170" cy="52" r="5" fill={GRAPHITE} data-id="element-417" data-mp="294p08oa2k65j" />
      <path d="M170 6 L216 52" stroke={COPPER} strokeWidth="1.5" data-id="element-418" data-mp="2dwggo5zigvhy" />
      {/* chip path arc off the contour */}
      <path d="M214 40 C 234 30, 246 20, 248 6" fill="none" stroke={COPPER} strokeWidth="1.5" data-id="element-419" data-mp="2iwb8cbr41p42" />
    </svg>;
}

/* D — Compact service items: driver and replacement screws */
export function ServiceItemsIllustration() {
  return <svg {...compactSvgProps} data-id="element-420" data-mp="1v6gwnxij26le">
      {/* driver */}
      <g stroke={NAVY} strokeWidth="1.5" data-id="element-421" data-mp="1v5p7p5jfr743">
        <path d="M4 12 H34 V46 H4 Z" fill={GRAPHITE} fillOpacity="0.85" data-id="element-422" data-mp="205k0xfqoo8sb" />
        <path d="M34 20 H206 V38 H34 Z" fill={GRAPHITE} fillOpacity="0.92" data-id="element-423" data-mp="206v69jzs1olj" />
        <path d="M206 16 H232 V42 H206 Z" fill={ALUMINUM} fillOpacity="0.65" data-id="element-424" data-mp="36kug8q68zzvm" />
      </g>
      <g stroke={COPPER} strokeWidth="1.5" data-id="element-425" data-mp="3bjnvoo53i0yf">
        <line x1="212" y1="29" x2="226" y2="29" data-id="element-426" data-mp="3bmjsg957ijla" />
        <line x1="219" y1="22" x2="219" y2="36" data-id="element-427" data-mp="2lvjs3j057gdk" />
      </g>
      {/* replacement screws */}
      <g stroke={NAVY} strokeWidth="1.5" data-id="element-428" data-mp="2gy9sxbmybqcj">
        <path d="M4 70 H58 V92 H4 Z" fill={ALUMINUM} fillOpacity="0.6" data-id="element-429" data-mp="2gx8fj0igqrrn" />
        <path d="M58 75 H80 L86 81 L80 87 H58 Z" fill="#FFFFFF" data-id="element-430" data-mp="2vtg2g8l9a7am" />
        <path d="M108 70 H162 V92 H108 Z" fill={ALUMINUM} fillOpacity="0.6" data-id="element-431" data-mp="2qvxsqfzezo1k" />
        <path d="M162 75 H184 L190 81 L184 87 H162 Z" fill="#FFFFFF" data-id="element-432" data-mp="2qttlbo2b46wj" />
      </g>
      <g stroke={COPPER} strokeWidth="1.5" data-id="element-433" data-mp="236d9dvzcmorr">
        <line x1="14" y1="81" x2="48" y2="81" data-id="element-434" data-mp="1y7io8ndwso4h" />
        <line x1="118" y1="81" x2="152" y2="81" data-id="element-435" data-mp="1y8tr8kzj5ruj" />
      </g>
      <TickRow y={102} from={200} to={260} step={20} height={5} data-id="element-436" data-mp="2cw51mjjz75t2" />
    </svg>;
}

/* Step pictograms — square field, same publication style */
const stepSvgProps = {
  'aria-hidden': true as const,
  focusable: 'false' as const,
  preserveAspectRatio: 'xMidYMid meet',
  viewBox: '0 0 96 96',
  className: 'block h-full w-full'
};

/* Step 1 — Turret / shank interface */
export function TurretInterfaceIllustration() {
  return <svg {...stepSvgProps} data-id="element-437" data-mp="3r29xwtl7pjd6">
      {/* turret body arc with pockets */}
      <g stroke={NAVY} strokeWidth="1.5" fill="none" data-id="element-438" data-mp="3vuuq5qogzoel">
        <path d="M14 12 H50 A34 34 0 0 1 50 84 H14 Z" fill={GRAPHITE} fillOpacity="0.12" data-id="element-439" data-mp="3vwe5nf2axvfb" />
        <path d="M50 24 A24 24 0 0 1 50 72" data-id="element-440" data-mp="3m48eg7jvji16" />
      </g>
      {/* pockets */}
      <g stroke={NAVY} strokeWidth="1.5" data-id="element-441" data-mp="doyf377dcqx3">
        <rect x="14" y="20" width="16" height="12" fill="#FFFFFF" data-id="element-442" data-mp="dpzshibuxphz" />
        <rect x="14" y="64" width="16" height="12" fill="#FFFFFF" data-id="element-443" data-mp="insv61dyc1di" />
      </g>
      {/* shank entering the interface */}
      <g stroke={NAVY} strokeWidth="1.5" data-id="element-444" data-mp="4p1jsmqh4tt2">
        <rect x="14" y="42" width="46" height="14" fill={GRAPHITE} fillOpacity="0.9" data-id="element-445" data-mp="4qcoqpvno7lr" />
        <rect x="60" y="44" width="14" height="10" fill={ALUMINUM} fillOpacity="0.7" data-id="element-446" data-mp="2pe9365cca5ew" />
      </g>
      <line x1="74" y1="49" x2="88" y2="49" stroke={COPPER} strokeWidth="1.5" data-id="element-447" data-mp="3l3nn8o0u29or" />
      {/* alignment + ticks */}
      <g stroke={NAVY} strokeOpacity="0.4" strokeWidth="1" strokeDasharray="4 4" data-id="element-448" data-mp="3g4u70nu5w4kw">
        <line x1="8" y1="49" x2="90" y2="49" data-id="element-449" data-mp="3g6x5lw9dr618" />
        <line x1="60" y1="16" x2="60" y2="82" data-id="element-450" data-mp="3c40jvwojmtt2" />
      </g>
      <g stroke={GRAPHITE} strokeOpacity="0.55" strokeWidth="1" data-id="element-451" data-mp="373w4k9zdg54f">
        <line x1="20" y1="88" x2="74" y2="88" data-id="element-452" data-mp="375qpvpek1z91" />
        <line x1="20" y1="88" x2="20" y2="83" data-id="element-453" data-mp="7paj84c9fed8" />
        <line x1="38" y1="88" x2="38" y2="85" data-id="element-454" data-mp="7nzg86qn2an6" />
        <line x1="56" y1="88" x2="56" y2="83" data-id="element-455" data-mp="3nfgcveoksxc" />
        <line x1="74" y1="88" x2="74" y2="85" data-id="element-456" data-mp="3np8aojada5o" />
      </g>
    </svg>;
}

/* Step 2 — Generic insert diamond with one controlled chip curl */
export function InsertFamilyIllustration() {
  return <svg {...stepSvgProps} data-id="element-457" data-mp="3q28n2mc6arco">
      <g stroke={NAVY} strokeWidth="1.5" data-id="element-458" data-mp="3q0xjonmn3lm3">
        <path d="M44 26 L74 56 L44 86 L14 56 Z" fill={ALUMINUM} fillOpacity="0.5" data-id="element-459" data-mp="rdten3nuzo0d" />
      </g>
      <path d="M44 38 L64 56 L44 74 L24 56 Z" fill="none" stroke={NAVY} strokeOpacity="0.55" strokeWidth="1" data-id="element-460" data-mp="mkqhkljvhwff" />
      <circle cx="44" cy="56" r="4" fill={GRAPHITE} data-id="element-461" data-mp="yvhh07sv6bao" />
      <path d="M44 26 L74 56" stroke={COPPER} strokeWidth="1.5" data-id="element-462" data-mp="yydgi0kd1818" />
      {/* controlled chip curl */}
      <path d="M62 30 C 74 24, 84 28, 86 38 C 88 48, 80 54, 72 50 C 66 47, 66 40, 72 38" fill="none" stroke={COPPER} strokeWidth="1.5" data-id="element-463" data-mp="13xgn3pfznmbb" />
      <g stroke={NAVY} strokeOpacity="0.35" strokeWidth="1" strokeDasharray="4 4" data-id="element-464" data-mp="1cvitkkihr75z">
        <line x1="44" y1="16" x2="44" y2="92" data-id="element-465" data-mp="1ht25vevmp9va" />
      </g>
    </svg>;
}

/* Step 3 — Application worksheet with a send cue */
export function ApplicationSheetIllustration() {
  return <svg {...stepSvgProps} data-id="element-466" data-mp="1rk566egqhkti">
      <g stroke={NAVY} strokeWidth="1.5" data-id="element-467" data-mp="1vmjs57fp90p0">
        <rect x="12" y="10" width="60" height="76" fill="#FFFFFF" data-id="element-468" data-mp="1vnl5jik6tz9w" />
      </g>
      <line x1="12" y1="26" x2="72" y2="26" stroke={NAVY} strokeWidth="1.5" data-id="element-469" data-mp="20l3eva24agif" />
      {/* abstract field rows */}
      <g data-id="element-470" data-mp="2d1l9b6pwmzns">
        <rect x="20" y="34" width="18" height="6" fill={ALUMINUM} fillOpacity="0.85" data-id="element-471" data-mp="2d24pahvzvdz6" />
        <rect x="42" y="34" width="22" height="6" fill={GRAPHITE} fillOpacity="0.16" data-id="element-472" data-mp="2myzo71w6jgdz" />
        <rect x="20" y="48" width="14" height="6" fill={ALUMINUM} fillOpacity="0.85" data-id="element-473" data-mp="2rr9923iftq9e" />
        <rect x="38" y="48" width="26" height="6" fill={GRAPHITE} fillOpacity="0.16" data-id="element-474" data-mp="30q4idogs3ggx" />
        <rect x="20" y="62" width="20" height="6" fill={ALUMINUM} fillOpacity="0.85" data-id="element-475" data-mp="35q8vd4ih9t2e" />
        <rect x="44" y="62" width="20" height="6" fill={GRAPHITE} fillOpacity="0.16" data-id="element-476" data-mp="1bc7z8rebbk60" />
      </g>
      <g stroke={NAVY} strokeOpacity="0.45" strokeWidth="1" data-id="element-477" data-mp="1gcdtmfbyvkrs">
        <line x1="20" y1="76" x2="64" y2="76" data-id="element-478" data-mp="3tllzxdmrj77z" />
      </g>
      {/* send cue */}
      <g stroke={COPPER} strokeWidth="1.5" fill="none" data-id="element-479" data-mp="3ot1bkrmmlmbu">
        <line x1="66" y1="88" x2="88" y2="88" data-id="element-480" data-mp="3os9hxmcl9yo7" />
        <path d="M80 82 L88 88 L80 94" data-id="element-481" data-mp="3ktk3rrjq8d3i" />
      </g>
    </svg>;
}